<!--
 * @Author: YuanYuan
 * @Date: 2024-02-22 18:22:16
 * @LastEditTime: 2025-07-31 11:17:52
 * @description: 折线图, 注意：使用组件时需要使用 LineXxx 来命名
-->
<template>
  <div :style="{height: height+'px'}" v-loading="loading">
    <div v-if="options.series && options.series.length" :id="id" class="full"></div>
    <ws-empty v-else :auto-fit="true" :description="noDataText || $t('common.noData')"></ws-empty>
  </div>
</template>

<script>
import chartMixins from './config/chartMixins.vue'
import 'echarts/lib/chart/line'
import { RectLegendStyle, GridStyle, TooltipStyle, RectMaker, XAxisCategoryStyle, YAxisValueStyle, ChartColors } from './config/commonOptions'
import { assign } from 'lodash-es'
import { numeralFormatter, toFixedFormatter } from '@/utils/base'

export default {
  mixins: [chartMixins],
  props: {
    options: {
      type: Object,
      default: () => ({
        xAxis: { data: [] },
        series: []
      })
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    height: {
      type: Number,
      default: 500
    },
    unit: {
      type: String,
      default: ''
    },
    digitType: {
      type: String,
      default: 'integer' // interger, float
    },
  },
  data() {
    return {
      digitFormatter: {
        integer: numeralFormatter,
        float: toFixedFormatter
      }
    }
  },
  watch: {
    options: {
      handler: function() {
        this.initComponent()
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    initComponent() {
      this.chart && this.chart.clear()
      this.chart && this.chart.dispose()
      this.chart = null
      if (this.options.series && this.options.series.length) {
        this.$nextTick(() => {
          this.chart = this.$echarts.init(document.getElementById(this.id))
          this.chart.setOption(this.chartOptions())
          this.attachEvents()
          this.legendEvents()
        })
      }
    },
    // 根据prop入参覆盖后的option
    chartOptions() {
      const { legend, xAxis, grid } = this.options
      const finalOption = {
        grid: assign({ ...GridStyle, top: 10, bottom: 90, containLabel: true }, grid),
        tooltip: {
          trigger: 'axis',
          confine: true, // 限制在图表的区域内
          ...TooltipStyle,
          formatter: params => {
            let result = `<div style="color: rgba(0,0,0,0.85);font-size:12px;margin-bottom:8px">${params[0].axisValue}</div>`
            for (let i = 0; i < params.length; i++) {
              const marker = RectMaker(params[i].color)
              const name = params[i].seriesName
              const value = params[i].value
              result += `<div style="color: rgba(0,0,0,0.65);font-size:12px">
                            <span>${marker}${name}${this.$t('common.colon')}${this.digitFormatter[this.digitType](value)}</span>
                            <span>${this.unit}</span>
                          </div>`
            }
            return result
          }
        },
        xAxis: assign({ type: 'category', ...XAxisCategoryStyle, data: [] }, xAxis),
        yAxis: { type: 'value', ...YAxisValueStyle, name: this.unit },
        dataZoom: [
          {
            type: 'inside', // 滚动条方式
            xAxisIndex: [0, 1]
          },
          {
            type: 'slider', // 鼠标滚轮方式
            height: 14,
            bottom: 50,
            handleSize: '130%',
            xAxisIndex: [0, 1]
          }
        ],
        color: this.seriesColor(),
        series: this.seriesFormatter()
      }
      if (this.showLegend) {
        finalOption.legend = assign({ ...RectLegendStyle, bottom: 0 }, legend)
      }
      return finalOption
    },
    seriesColor() {
      if (this.options.series && this.options.series.length) {
        const colors = this.options.series.map(item => item.color).filter(color => color)
        return colors.length ? colors : ChartColors
      } else {
        return ChartColors
      }
    },
    seriesFormatter() {
      if (this.options.series && this.options.series.length) { 
        return this.options.series.map(item => {
          return assign({
            type: 'line',
            symbolSize: 0,
            symbol: 'none'
          }, item)
        })
      } else {
        return []
      }
    }
  }
}
</script>
